import Close from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip, { ChipPropsColorOverrides } from '@mui/material/Chip';
import { InputBaseProps } from '@mui/material/InputBase';
import Paper, { PaperProps } from '@mui/material/Paper';
import type { PopperPlacementType, PopperProps } from '@mui/material/Popper';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { OverridableStringUnion } from '@mui/types';
import { t } from 'i18next';
import { get } from 'lodash-es';
import React, { ReactNode, useMemo, useRef, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';

import { SelectOption } from './Parts/SelectOption';

interface FormFieldProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  name: string;
  list: Array<Record<string, any> | any>;
  label?: string;
  isRequired?: boolean;
  sx?: SxProps<Theme>;
  clearable?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  chipsColor?: OverridableStringUnion<
    'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    ChipPropsColorOverrides
  >;
  loading?: boolean;
  noOptionsText?: string | ReactNode;
  inputProps?: InputBaseProps['inputProps'];
  getValue: (value: any) => string; // Is used for the equal comparison of the values
  getLabel: (value: any) => string; // Is used for the label of the option
  groupBy?: (value: any) => string; // Is used for the groupBy of the option
  isCheckEnabled?: boolean;
  disableCloseOnSelect?: boolean;
  limitTags?: number;
  placeholder?: string;
  isInline?: boolean;
  labelColor?: string;
  isShrinkingLabel?: boolean;
  chipMaxWidth?: string;
  isChipSelection?: boolean;
  onInput?: (event: React.SyntheticEvent, value: string) => void;
  // Default value to be set when the clear button is clicked
  // When the value type is an array, this can lead to errors with yup validation
  clearDefaultValue?: any;
  isDenseText?: boolean;
  onClick?: () => void;
  onSelect?: (value: any) => void;
  clearOnChange?: (value: any) => boolean;
  onDelete?: (option: any) => void;
  fixedOptions?: Array<any>;
  disablePortal?: boolean;
  freeSolo?: boolean;
  autoSelect?: boolean;
  dataUi?: string;
  popperPlacement?: PopperPlacementType;
  listboxWidth?: number | string;
  popperModifiers?: PopperProps['modifiers'];
  dropdownFooter?: (close: () => void) => ReactNode;
}

const EMPTY_MULTIPLE_SELECTION: never[] = [];

const AutocompleteFormField = <TFieldValues extends FieldValues = FieldValues>({
  control,
  errors,
  name,
  list,
  label,
  clearable = false,
  getLabel,
  getValue,
  groupBy,
  chipsColor,
  isRequired,
  disabled = false,
  multiple = false,
  inputProps,
  loading = false,
  sx,
  isCheckEnabled = true,
  disableCloseOnSelect = true,
  noOptionsText = t('form_fields.no_options') || '',
  limitTags,
  placeholder,
  isInline = false,
  labelColor = 'secondary',
  isShrinkingLabel = false,
  chipMaxWidth = '100%',
  isChipSelection = true,
  onInput = () => {},
  clearDefaultValue = null,
  isDenseText = true,
  onClick = () => {},
  onSelect = () => {},
  clearOnChange = () => true,
  onDelete,
  fixedOptions,
  disablePortal = true,
  freeSolo = false,
  dataUi,
  popperPlacement,
  listboxWidth,
  popperModifiers,
  dropdownFooter,
}: FormFieldProps<TFieldValues>) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownFooterRef = useRef(dropdownFooter);
  const footerElementRef = useRef<HTMLDivElement | null>(null);
  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const isMovingFocusToFooterRef = useRef(false);
  const setIsOpenRef = useRef(setIsOpen);
  dropdownFooterRef.current = dropdownFooter;
  setIsOpenRef.current = setIsOpen;

  const hasDropdownFooter = Boolean(dropdownFooter);
  const DropdownPaper = useMemo(() => {
    if (!hasDropdownFooter) {
      return undefined;
    }

    return function DropdownPaper({ children, ...paperProps }: PaperProps) {
      const handleFooterBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        const nextFocusedElement = event.relatedTarget;
        const focusStaysInsideDropdown =
          nextFocusedElement instanceof Node &&
          (footerElementRef.current?.contains(nextFocusedElement) === true ||
            inputElementRef.current === nextFocusedElement);

        if (!focusStaysInsideDropdown) {
          setIsOpenRef.current(false);
        }
      };

      const handleFooterKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const shouldReturnFocusToInput =
          event.key === 'Escape' || (event.key === 'Tab' && event.shiftKey);

        if (shouldReturnFocusToInput) {
          event.preventDefault();
          event.stopPropagation();
          inputElementRef.current?.focus();
        }
      };

      return (
        <Paper {...paperProps}>
          {children}
          <Box
            data-test-id="autocomplete-dropdown-footer"
            onBlur={handleFooterBlur}
            onKeyDown={handleFooterKeyDown}
            onMouseDown={(event) => event.preventDefault()}
            ref={footerElementRef}
            role="presentation"
            sx={{
              bgcolor: 'background.paper',
              borderColor: 'divider',
              borderTop: 1,
              bottom: 0,
              p: 0.5,
              position: 'sticky',
            }}
            tabIndex={-1}
          >
            {dropdownFooterRef.current?.(() => setIsOpenRef.current(false))}
          </Box>
        </Paper>
      );
    };
  }, [hasDropdownFooter]);

  const isFixedOption = (option: any) =>
    fixedOptions?.some((fixed) => getValue(fixed) === getValue(option)) ?? false;

  const emptySelection = multiple ? EMPTY_MULTIPLE_SELECTION : null;

  return (
    <Controller
      control={control}
      name={name as FieldPath<TFieldValues>}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          disableCloseOnSelect={disableCloseOnSelect && multiple}
          loading={loading}
          open={isOpen}
          disablePortal={disablePortal}
          onOpen={() => {
            onClick?.();
            setIsOpen(true);
          }}
          onClose={(_event, reason) => {
            if (reason === 'blur' && isMovingFocusToFooterRef.current) {
              return;
            }

            setIsOpen(false);
          }}
          noOptionsText={
            <Box
              onClick={() => {
                setIsOpen(false);
              }}
            >
              {noOptionsText}
            </Box>
          }
          limitTags={limitTags}
          disableClearable={!clearable}
          options={list}
          fullWidth
          sx={{ ...sx }}
          slotProps={{
            paper: {
              elevation: 4,
              ...(listboxWidth && { sx: { width: listboxWidth } }),
            },
            popper:
              popperPlacement || popperModifiers || listboxWidth
                ? {
                    ...(listboxWidth && { style: { width: listboxWidth } }),
                    ...(popperPlacement && { placement: popperPlacement }),
                    ...(popperModifiers && { modifiers: popperModifiers }),
                  }
                : undefined,
          }}
          {...(DropdownPaper && { slots: { paper: DropdownPaper } })}
          multiple={multiple}
          disabled={disabled}
          getOptionLabel={(item) => getLabel(item)}
          groupBy={groupBy}
          filterSelectedOptions={false}
          isOptionEqualToValue={(option, value) => {
            return getValue(option) === getValue(value);
          }}
          onInputChange={onInput}
          renderValue={
            multiple
              ? (selectedValue, getItemProps) => {
                  if (!Array.isArray(selectedValue)) {
                    return null;
                  }

                  const value: readonly string[] = selectedValue;
                  const numTags = value.length;
                  const chipWidthPercentage = limitTags
                    ? // 98% is used here to give some room for the hidden tags count
                      98 / Math.min(value.length, limitTags)
                    : 100;

                  return (
                    <>
                      {value.slice(0, limitTags).map((option: string, index: number) => {
                        const fixed = isFixedOption(option);
                        const { onDelete: tagOnDelete, ...restTagProps } = getItemProps({
                          index,
                        });

                        return isChipSelection ? (
                          <Box
                            sx={{
                              maxWidth: chipMaxWidth || `${chipWidthPercentage}%`,
                              '& .MuiAutocomplete-tag': {
                                maxWidth: '100%',
                              },
                            }}
                            key={`autocomplete-chip-${option}--${index}`}
                          >
                            <Tooltip title={getLabel(option)}>
                              <Chip
                                size={'small'}
                                color={chipsColor || 'primary'}
                                label={getLabel(option)}
                                deleteIcon={<Close />}
                                {...restTagProps}
                                {...(fixed ? {} : { onDelete: tagOnDelete })}
                              />
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography key={`autocomplete-chip-${option}--${index}`}>
                            {getLabel(option)}
                          </Typography>
                        );
                      })}
                      {/* Show count of hidden tags */}
                      {limitTags && (
                        <Typography variant="body2">
                          <Box component="span" sx={{ pl: 0.5 }}>
                            {numTags > limitTags && ` +${numTags - limitTags}`}
                          </Box>
                        </Typography>
                      )}
                    </>
                  );
                }
              : undefined
          }
          renderInput={(params) => {
            const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
              const shouldFocusFooter = isOpen && event.key === 'Tab' && !event.shiftKey;

              if (shouldFocusFooter) {
                event.preventDefault();
                event.stopPropagation();
                isMovingFocusToFooterRef.current = true;
                const focusableFooterElement =
                  footerElementRef.current?.querySelector<HTMLElement>(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
                  );
                (focusableFooterElement ?? footerElementRef.current)?.focus();
                isMovingFocusToFooterRef.current = false;

                return;
              }

              params.inputProps.onKeyDown?.(event);
            };

            return (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isInline ? 'row' : 'column',
                  width: '100%',
                  alignItems: isInline ? 'center' : 'flex-start',
                }}
              >
                {!isShrinkingLabel && label && (
                  <FormFieldLabel
                    label={`${label}${isRequired ? ' *' : ''}`}
                    labelColor={labelColor}
                    sx={{ mr: isInline ? 1 : '' }}
                  />
                )}
                <TextField
                  {...params}
                  size={'small'}
                  margin={isDenseText ? 'dense' : 'none'}
                  placeholder={placeholder}
                  variant="outlined"
                  className={isRequired && !disabled ? 'required' : ''}
                  error={Boolean(get(errors, name))}
                  data-test-id={get(inputProps, 'data-test-id', '')}
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      ...(dataUi && { 'data-ui': dataUi }),
                      ...(hasDropdownFooter && { onKeyDown: handleInputKeyDown }),
                    },
                  }}
                  {...(hasDropdownFooter && { inputRef: inputElementRef })}
                  sx={(theme) => ({
                    background: theme.semantic.surfaceInputSolid,
                    borderRadius: 1,
                    '& .MuiInputBase-root': {
                      flexFlow: 'row',
                    },
                    ...inputProps?.sx,
                  })}
                  label={isShrinkingLabel ? `${label}${isRequired ? ' *' : ''}` : ''}
                />
              </Box>
            );
          }}
          renderOption={(props, option, { inputValue, selected }) => (
            <SelectOption
              key={`select-option-${option.id || option.name || option.value || option}`}
              isCheckEnabled={isCheckEnabled && multiple}
              selected={selected}
              option={option}
              getLabel={getLabel}
              inputValue={inputValue}
              props={{ ...props, style: { padding: '6px' } }}
              onDelete={
                onDelete
                  ? () => {
                      onDelete?.(option);
                    }
                  : undefined
              }
            />
          )}
          getOptionDisabled={
            fixedOptions?.length ? (option) => isFixedOption(option) : undefined
          }
          onChange={(event, values, reason) => {
            if (reason === 'clear') {
              const kept =
                multiple && fixedOptions?.length ? [...fixedOptions] : clearDefaultValue;
              onChange(kept);
              onSelect(kept);
            } else {
              let newValues = values;
              if (multiple && fixedOptions?.length) {
                const missingFixed = fixedOptions.filter(
                  (fixed) => !values.some((v: any) => getValue(v) === getValue(fixed)),
                );
                newValues = [...missingFixed, ...values];
              }
              if (clearOnChange(newValues)) {
                onChange(newValues);
                onSelect(newValues);
              }
            }
          }}
          value={value ?? emptySelection}
          freeSolo={freeSolo}
          // FreeSolo will not send the input text if the input focus is lost,
          // so we need to set autoSelect to true to make this behavior work like other input fields
          autoSelect={freeSolo}
        />
      )}
    />
  );
};

export { AutocompleteFormField };
